import React from "react"
import { PageTitle } from "@common-metronic/layout/core"
import MyAlert from "@common-utils/MyAlert"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import { ROUTING_BREADCRUMBS_GESTION_BASE_DOCUMENTAIRE } from "../../gestion/routing/routing-breadcrumbs"
import StatutFolder from "./_StatutFolder"
import { documentsEmbaucheConfigRequest, IDocumentsEmbaucheEntiteModel } from "./_requests"

/**
 * Administration des « documents d'embauche » (modèles Drive) : pour chaque entité, les dossiers ETAM et
 * CADRE et leurs documents, gérés directement dans l'application (consultation, téléversement, corbeille).
 */
const DocumentsEmbaucheAdmin = () => {
     const [entites, setEntites] = React.useState<IDocumentsEmbaucheEntiteModel[] | null>(null)
     const [loading, setLoading] = React.useState<boolean>(true)
     const [error, setError] = React.useState<string | null>(null)
     const [openKey, setOpenKey] = React.useState<string | null>(null)
     const [search, setSearch] = React.useState<string>("")

     React.useEffect(() => {
          documentsEmbaucheConfigRequest()
               .then(r => setEntites(r.data.entites))
               .catch(e => setError(e?.response?.data?.detail ?? "Impossible de charger les documents d'embauche."))
               .finally(() => setLoading(false))
     }, [])

     const q = search.trim().toLowerCase()

     // Une entité est visible si son nom correspond OU si elle contient un document dont le titre / nom de
     // fichier correspond. Lorsque la recherche vise un document (et non l'entité), on ne garde que les
     // statuts contenant au moins un document correspondant.
     const filtered = React.useMemo(() => {
          if (!entites) return []
          if (!q) return entites.map(e => ({ entite: e, docQuery: "" }))
          const matchesDoc = (d: { title: string | null; fileName: string | null }) =>
               (d.title ?? "").toLowerCase().includes(q) || (d.fileName ?? "").toLowerCase().includes(q)
          return entites
               .map(e => {
                    const labelMatch = e.label.toLowerCase().includes(q)
                    if (labelMatch) return { entite: e, docQuery: "" }
                    const hasDocs = e.statuts.some(s => s.documents.some(matchesDoc))
                    // docQuery non vide => StatutFolder filtre les documents affichés sur cette recherche.
                    return hasDocs ? { entite: e, docQuery: q } : null
               })
               .filter((x): x is { entite: (typeof entites)[number]; docQuery: string } => x !== null)
     }, [entites, q])

     return (
          <>
               <PageTitle breadcrumbs={ROUTING_BREADCRUMBS_GESTION_BASE_DOCUMENTAIRE}>Documents d'embauche</PageTitle>

               <div className="card">
                    <div className="card-header align-items-center">
                         <div>
                              <h3 className="card-title mb-1">Documents d'embauche par entité</h3>
                              <div className="text-muted fs-7">
                                   Modèles envoyés aux nouveaux collaborateurs, organisés par entité de rattachement puis par statut (ETAM / Cadre).
                              </div>
                         </div>
                         <div className="card-toolbar">
                              <div className="position-relative">
                                   <i className="fas fa-search position-absolute top-50 translate-middle-y ms-3 text-muted" />
                                   <input
                                        type="text"
                                        className="form-control form-control-sm form-control-solid ps-10 w-250px"
                                        placeholder="Rechercher une entité ou un document…"
                                        value={search}
                                        onChange={e => setSearch(e.target.value)}
                                   />
                              </div>
                         </div>
                    </div>
                    <div className="card-body">
                         {loading && (
                              <div className="d-flex justify-content-center py-10">
                                   <MySimpleSpinner size={"md"} />
                              </div>
                         )}
                         {!loading && error && (
                              <MyAlert type={"danger"} isDismissible={false}>
                                   <>{error}</>
                              </MyAlert>
                         )}
                         {!loading && !error && filtered.length === 0 && <div className="text-muted">Aucune entité.</div>}

                         {!loading && !error && (
                              <div className="d-flex flex-column gap-3">
                                   {filtered.map(({ entite, docQuery }) => {
                                        // Recherche active => on déplie automatiquement pour montrer les résultats.
                                        const isOpen = q ? true : openKey === entite.key
                                        const total = entite.statuts.reduce((n, s) => n + s.documents.length, 0)
                                        return (
                                             <div key={entite.key} className="border border-gray-300 rounded">
                                                  <div
                                                       className="d-flex align-items-center justify-content-between p-4 cursor-pointer"
                                                       onClick={() => !q && setOpenKey(isOpen ? null : entite.key)}
                                                  >
                                                       <div className="d-flex align-items-center gap-3">
                                                            <i className={`fas ${isOpen ? "fa-folder-open text-primary" : "fa-folder text-gray-500"} fs-2`} />
                                                            <div>
                                                                 <div className="fw-bold fs-5 text-gray-900">{entite.label}</div>
                                                                 <div className="text-muted fs-8">
                                                                      {entite.statuts.map(s => `${s.statutLabel} : ${s.documents.length}`).join("  ·  ")}
                                                                 </div>
                                                            </div>
                                                       </div>
                                                       <div className="d-flex align-items-center gap-3">
                                                            <span className="badge badge-light-primary">{total} document{total > 1 ? "s" : ""}</span>
                                                            {!q && <i className={`fas ${isOpen ? "fa-chevron-up" : "fa-chevron-down"} text-muted`} />}
                                                       </div>
                                                  </div>

                                                  {isOpen && (
                                                       <div className="border-top border-gray-300 p-4">
                                                            <div className="row g-4">
                                                                 {entite.statuts.map(statut => (
                                                                      <div className="col-lg-6" key={statut.statut}>
                                                                           <div className="d-flex align-items-center mb-3">
                                                                                <span className={`badge ${statut.statut === "CADRE" ? "badge-light-info" : "badge-light-success"} fs-7 me-2`}>
                                                                                     {statut.statutLabel}
                                                                                </span>
                                                                                <span className="text-muted fs-8">{statut.documents.length} document(s)</span>
                                                                           </div>
                                                                           <StatutFolder entityKey={entite.key} statut={statut} docQuery={docQuery} />
                                                                      </div>
                                                                 ))}
                                                            </div>
                                                       </div>
                                                  )}
                                             </div>
                                        )
                                   })}
                              </div>
                         )}
                    </div>
               </div>
          </>
     )
}

export default DocumentsEmbaucheAdmin
